import React, { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Download, Loader2, ShieldCheck, ArrowLeft, BadgeCheck } from 'lucide-react'
import { verifyCertificate, type CertificateVerify } from '../api/lessonBuilderApi'

// Trang CHỨNG CHỈ hoàn thành khoá học — CÔNG KHAI (tra cứu bằng mã), in / lưu PDF được.
// SEO/UX: dùng cho cả học viên xem chứng chỉ của mình và người khác xác minh.
const fmtDate = (iso?: string) => {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return `ngày ${d.getDate()} tháng ${d.getMonth() + 1} năm ${d.getFullYear()}`
  } catch {
    return ''
  }
}

// Font AN TOÀN tiếng Việt (đủ dấu, không lỗi glyph): Inter đã nạp sẵn; serif Georgia/Times có VN;
// mono chỉ hiển thị mã (ASCII) nên an toàn tuyệt đối.
const SANS = "'Inter', system-ui, 'Segoe UI', Roboto, Arial, sans-serif"
const SERIF = "Georgia, 'Times New Roman', 'DejaVu Serif', serif"
const MONO = "'JetBrains Mono', 'Courier New', monospace"

// Nhánh nguyệt quế vàng (lá đặt theo cung). mirror → lật để ôm hai bên chữ.
const LEAVES: [number, number, number][] = [
  [22, 103, -46], [30, 99, 42], [18, 85, -42], [28, 81, 50], [15, 67, -36], [26, 63, 54],
  [16, 49, -30], [26, 45, 58], [19, 29, -24], [27, 25, 62], [29, 10, 74],
]
const LaurelBranch: React.FC<{ mirror?: boolean }> = ({ mirror }) => (
  <svg viewBox="0 0 64 120" className="w-full h-full" aria-hidden>
    <g transform={mirror ? 'scale(-1,1) translate(-64,0)' : undefined}>
      <path d="M32 118 C 15 95 13 56 30 8" fill="none" stroke="#b98f38" strokeWidth={2} />
      {LEAVES.map(([x, y, r], i) => (
        <ellipse key={i} cx={x} cy={y} rx={6.6} ry={3} fill="#c6a24a" transform={`rotate(${r} ${x} ${y})`} />
      ))}
    </g>
  </svg>
)

// Con dấu "đã xác thực" — huy hiệu khiên + dấu tick, viền chấm vàng.
const Seal: React.FC = () => {
  const dots = Array.from({ length: 28 }, (_, i) => {
    const a = (i / 28) * Math.PI * 2
    return { x: 50 + 34 * Math.cos(a), y: 50 + 34 * Math.sin(a) }
  })
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden>
      {dots.map((d, i) => <circle key={i} cx={d.x} cy={d.y} r={2.4} fill="#d4b25f" />)}
      <circle cx={50} cy={50} r={30} fill="#12336e" stroke="#e7c877" strokeWidth={2} />
      <circle cx={50} cy={50} r={24} fill="none" stroke="#4a68a8" strokeWidth={1} />
      <path d="M50 33 L64 39 V52 C64 64 50 70 50 70 C50 70 36 64 36 52 V39 Z" fill="none" stroke="#e7c877" strokeWidth={2.4} />
      <path d="M43 51 l5 5 10-11" fill="none" stroke="#f2d488" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// Huy hiệu khiên (sách + sao) + nguyệt quế hai bên.
const Emblem: React.FC = () => (
  <div className="relative" style={{ width: 120, height: 96, marginBottom: 2 }}>
    <div style={{ position: 'absolute', left: -6, top: 14, width: 38, height: 70 }}><LaurelBranch mirror /></div>
    <div style={{ position: 'absolute', right: -6, top: 14, width: 38, height: 70 }}><LaurelBranch /></div>
    <svg viewBox="0 0 90 92" style={{ position: 'absolute', left: 15, top: 0, width: 90, height: 92 }} aria-hidden>
      <path d="M45 8 L74 18 V45 C74 66 45 84 45 84 C45 84 16 66 16 45 V18 Z" fill="#12336e" stroke="#c9a24a" strokeWidth={3} />
      <path d="M45 13 L69 21 V45 C69 63 45 78 45 78 C45 78 21 63 21 45 V21 Z" fill="none" stroke="#e7c877" strokeWidth={1} />
      <path d="M28 45 Q45 39 45 42 L45 59 Q28 56 28 59 Z" fill="#ffffff" />
      <path d="M62 45 Q45 39 45 42 L45 59 Q62 56 62 59 Z" fill="#eef2fb" />
      <line x1={45} y1={42} x2={45} y2={59} stroke="#c9a24a" strokeWidth={1.1} />
      <path d="M45 25 l2.5 5.1 5.6 .8 -4.05 3.95 .95 5.6 -5-2.63 -5 2.63 .95-5.6 -4.05-3.95 5.6-.8 Z" fill="#eab945" />
    </svg>
  </div>
)

// Hoạ tiết góc (xoay theo từng góc).
const Corner: React.FC<{ deg: number; style: React.CSSProperties }> = ({ deg, style }) => (
  <div style={{ ...style, transform: `rotate(${deg}deg)` }} aria-hidden>
    <svg viewBox="0 0 52 52" className="w-full h-full">
      <path d="M4 50 C4 24 24 4 50 4" fill="none" stroke="#bd9539" strokeWidth={1.6} />
      <path d="M12 50 C12 30 30 12 50 12" fill="none" stroke="#d2b463" strokeWidth={1} />
      <circle cx={6} cy={6} r={2.4} fill="#bd9539" />
      <path d="M6 20 C6 12 12 6 20 6" fill="none" stroke="#bd9539" strokeWidth={1.4} />
    </svg>
  </div>
)

// Hoạ tiết nền mờ: atom + đường cong.
const Watermark: React.FC = () => {
  const Atom: React.FC<{ x: number; y: number; s: number }> = ({ x, y, s }) => (
    <g transform={`translate(${x},${y}) scale(${s})`} stroke="#dde4f1" strokeWidth={1.6} fill="none">
      <ellipse rx={26} ry={10} /><ellipse rx={26} ry={10} transform="rotate(60)" /><ellipse rx={26} ry={10} transform="rotate(120)" />
      <circle r={3.2} fill="#dde4f1" stroke="none" />
    </g>
  )
  const curves = Array.from({ length: 6 }, (_, k) => {
    const y = 120 + k * 80
    return <path key={k} d={`M560 ${y} C 700 ${y - 40}, 860 ${y + 30}, 1000 ${y - 10}`} fill="none" stroke="#dbe3f2" strokeWidth={1.4} />
  })
  return (
    <svg viewBox="0 0 1000 707" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden>
      {curves}
      <Atom x={120} y={120} s={1} /><Atom x={900} y={150} s={0.9} /><Atom x={150} y={560} s={0.85} /><Atom x={880} y={560} s={1} />
    </svg>
  )
}

const CertificatePage: React.FC = () => {
  const { code } = useParams()
  const [data, setData] = useState<CertificateVerify | null | undefined>(undefined)
  const sheetRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [saving, setSaving] = useState(false)
  const [scale, setScale] = useState(1)

  // Tải chứng chỉ dưới dạng ẢNH PNG chất lượng cao (1 ảnh duy nhất — không tràn trang như khi in).
  const downloadCert = async () => {
    const node = sheetRef.current
    if (!node) return
    setSaving(true)
    try {
      const { toPng } = await import('html-to-image')   // nạp động khi cần → không nặng bundle chính
      // ÉP đúng khổ A4 NGANG (297×210): aspect-ratio khi clone hay bị cắt → truyền width/height tường minh.
      // pixelRatio 3 để nét (xuất ~3000×2121, tỉ lệ 297:210).
      const w = 1000
      const h = 707
      const url = await toPng(node, {
        width: w,
        height: h,
        pixelRatio: 3,
        backgroundColor: '#0e2a5e',
        cacheBust: true,
        style: { width: `${w}px`, height: `${h}px`, transform: 'scale(1)' },   // cố định kích thước clone → không cắt/tràn
      })
      const a = document.createElement('a')
      a.download = `Chung-chi-${code || 'VatLi365'}.png`
      a.href = url
      a.click()
    } catch {
      alert('Không tạo được ảnh chứng chỉ. Vui lòng thử lại.')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    document.title = 'Chứng chỉ hoàn thành | VatLi365'
    if (!code) { setData({ valid: false }); return }
    verifyCertificate(code).then(setData).catch(() => setData({ valid: false }))
  }, [code])

  useEffect(() => {
    const updateScale = () => {
      if (wrapperRef.current) {
        const w = wrapperRef.current.offsetWidth
        setScale(Math.min(w / 1000, 1))
      }
    }
    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [data])

  if (data === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">Đang tra cứu chứng chỉ…</div>
  }

  if (!data.valid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] px-4 text-center">
        <ShieldCheck className="w-14 h-14 text-gray-300 mb-3" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">Không tìm thấy chứng chỉ</h1>
        <p className="text-slate-500 mb-5">Mã chứng chỉ <span className="font-mono font-bold">{code}</span> không hợp lệ hoặc đã bị thu hồi.</p>
        <Link to="/khoa-hoc" className="text-blue-600 font-semibold">← Khám phá khoá học</Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] py-8 px-4 print:bg-white print:p-0">
      {/* Thanh công cụ — ẩn khi in */}
      <div className="max-w-[1000px] mx-auto mb-5 flex items-center gap-3 print:hidden">
        <Link to="/khoa-hoc" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
          <ArrowLeft size={16} /> Khoá học
        </Link>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
          <BadgeCheck size={14} /> Chứng chỉ hợp lệ · mã {data.code}
        </span>
        <button
          onClick={downloadCert}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold shadow hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {saving ? 'Đang tạo ảnh…' : 'Tải chứng chỉ'}
        </button>
      </div>

      {/* Khung chứng chỉ A4 ngang — viền navy/vàng, huy hiệu, nguyệt quế, con dấu */}
      <div ref={wrapperRef} className="max-w-[1000px] mx-auto shadow-xl print:shadow-none overflow-hidden relative print:overflow-visible" style={{ aspectRatio: '297 / 210', borderRadius: 6 }}>
        <div
          ref={sheetRef}
          className="cert-sheet absolute top-0 left-0 origin-top-left"
          style={{
            width: 1000, height: 707,
            transform: `scale(${scale})`,
            background: '#0e2a5e', borderRadius: 6,
            fontFamily: SANS, color: '#12244f',
            WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
          } as React.CSSProperties}
        >
          {/* viền vàng mảnh sát mép navy */}
        <div className="absolute" style={{ inset: '1.5%', border: '1.5px solid #c9a24a', borderRadius: 5 }} />

        {/* nền kem */}
        <div className="absolute overflow-hidden" style={{ inset: '2.2%', background: 'radial-gradient(130% 130% at 50% -10%,#ffffff,#f6f3e9)', borderRadius: 4 }}>
          <Watermark />

          {/* khung vàng đôi */}
          <div className="absolute" style={{ inset: '3.4%', border: '2px solid #bd9539', borderRadius: 9 }} />
          <div className="absolute" style={{ inset: '4.2%', border: '1px solid #d2b463', borderRadius: 7 }} />

          {/* hoạ tiết 4 góc */}
          <Corner deg={0} style={{ position: 'absolute', top: '3%', left: '3%', width: 52, height: 52 }} />
          <Corner deg={90} style={{ position: 'absolute', top: '3%', right: '3%', width: 52, height: 52 }} />
          <Corner deg={180} style={{ position: 'absolute', bottom: '3%', right: '3%', width: 52, height: 52 }} />
          <Corner deg={270} style={{ position: 'absolute', bottom: '3%', left: '3%', width: 52, height: 52 }} />

          {/* nội dung */}
          <div className="absolute inset-0 flex flex-col items-center text-center" style={{ padding: '5.5% 11% 0' }}>
            <Emblem />

            {/* thương hiệu */}
            <div className="flex items-center gap-2" style={{ marginTop: 2 }}>
              <svg width={16} height={20} viewBox="0 0 16 20" aria-hidden>
                <circle cx={8} cy={6} r={5} fill="none" stroke="#c9a24a" strokeWidth={1.6} />
                <circle cx={8} cy={6} r={2} fill="#c9a24a" />
                <path d="M5 10 L4 19 L8 16 L12 19 L11 10 Z" fill="#12336e" />
              </svg>
              <span style={{ fontSize: 23, fontWeight: 800, letterSpacing: '.3px', color: '#16305f' }}>VatLi365 · AI-PhysicsBook</span>
            </div>

            {/* tiêu đề */}
            <div style={{ fontSize: 46, fontWeight: 800, letterSpacing: '.5px', color: '#14265f', marginTop: 10, lineHeight: 1.05 }}>CHỨNG CHỈ HOÀN THÀNH</div>
            <div className="flex items-center" style={{ gap: 12, marginTop: 6, color: '#bd9539' }}>
              <span style={{ height: 1, width: 80, background: '#cdb26a' }} />
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 5, whiteSpace: 'nowrap' }}>◆ CERTIFICATE OF COMPLETION ◆</span>
              <span style={{ height: 1, width: 80, background: '#cdb26a' }} />
            </div>

            {/* người nhận */}
            <div style={{ marginTop: 26, fontSize: 15, color: '#6b7488' }}>Chứng nhận học viên</div>
            <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 56, color: '#12244f', lineHeight: 1.1, marginTop: 2 }}>{data.learner_name || '—'}</div>
            <div className="flex items-center" style={{ gap: 6, marginTop: 6 }}>
              <span style={{ height: 2, width: 150, background: '#d2b463' }} />
              <span style={{ width: 6, height: 6, background: '#bd9539', transform: 'rotate(45deg)', display: 'inline-block' }} />
              <span style={{ height: 2, width: 150, background: '#d2b463' }} />
            </div>

            {/* khoá học + nguyệt quế */}
            <div style={{ marginTop: 20, fontSize: 15, color: '#6b7488' }}>đã hoàn thành khóa học</div>
            <div className="flex items-center justify-center" style={{ gap: 14, marginTop: 4 }}>
              <div style={{ width: 52, height: 92, flexShrink: 0 }}><LaurelBranch /></div>
              <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '.5px', color: '#1c50d8', lineHeight: 1.05 }}>{data.course_title || 'Khoá học'}</div>
              <div style={{ width: 52, height: 92, flexShrink: 0 }}><LaurelBranch mirror /></div>
            </div>
          </div>

          {/* chân trang */}
          <div className="absolute flex items-end justify-between" style={{ left: '8%', right: '8%', bottom: '6%', gap: 10 }}>
            <div className="text-left" style={{ flex: 1 }}>
              <div style={{ height: 2, width: '70%', maxWidth: 150, background: '#d2b463', marginBottom: 6 }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: '#16305f' }}>{data.issuer_name || 'VatLi365'}</div>
              <div style={{ fontSize: 11, color: '#7c8598', marginTop: 1 }}>Đơn vị cấp · {fmtDate(data.issued_at)}</div>
            </div>

            <div className="flex flex-col items-center" style={{ flexShrink: 0 }}>
              <div style={{ width: 74, height: 74 }}><Seal /></div>
              <div className="flex items-center" style={{ gap: 5, marginTop: 4 }}>
                <svg width={12} height={10} viewBox="0 0 12 10" aria-hidden><path d="M11 1 C5 1 3 5 1 9" fill="none" stroke="#bd9539" strokeWidth={1} /><circle cx={3} cy={6} r={1.3} fill="#bd9539" /><circle cx={5} cy={3.5} r={1.3} fill="#bd9539" /></svg>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#16305f', whiteSpace: 'nowrap' }}>ĐÃ XÁC THỰC</span>
                <svg width={12} height={10} viewBox="0 0 12 10" aria-hidden><path d="M1 1 C7 1 9 5 11 9" fill="none" stroke="#bd9539" strokeWidth={1} /><circle cx={9} cy={6} r={1.3} fill="#bd9539" /><circle cx={7} cy={3.5} r={1.3} fill="#bd9539" /></svg>
              </div>
            </div>

            <div className="text-right" style={{ flex: 1 }}>
              <div style={{ height: 2, width: '70%', maxWidth: 150, background: '#d2b463', marginBottom: 6, marginLeft: 'auto' }} />
              <div style={{ fontSize: 11, color: '#7c8598' }}>Mã xác minh</div>
              <div style={{ fontFamily: MONO, fontWeight: 800, fontSize: 16, color: '#16305f', letterSpacing: '.5px' }}>{data.code}</div>
              <div style={{ fontSize: 10, color: '#a2a9b8', marginTop: 1 }}>ebook.vatli365.vn/chung-chi/{data.code}</div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <p className="max-w-[1000px] mx-auto mt-4 text-center text-xs text-slate-400 print:hidden">
        Xác minh tính xác thực bằng cách truy cập <span className="font-mono">ebook.vatli365.vn/chung-chi/{data.code}</span>
      </p>

      {/* In: A4 ngang, ẩn mọi thứ ngoài khung chứng chỉ */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 6mm; }
          body { background: #fff !important; }
          .cert-sheet { transform: scale(1) !important; box-shadow: none !important; }
          /* Giữ nền/màu trang trí khi in (navy, vàng, con dấu) */
          .cert-sheet, .cert-sheet * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  )
}

export default CertificatePage
